import * as React from 'react'
import {
  encodeEditedScalar,
  type EntityResolution,
  type MergeConflicts,
  type TaggedRel,
  type TaggedValue
} from '@axilon-apps/domains-ontology-ui-state'
import { DiffRow, DiffTable, KindBadge } from '../DiffTable'
import { ConflictDiffGrid, type ConflictLabelsRow } from './ConflictDiffGrid'
import type { ConflictValueEditorState } from './ConflictValueEditor'
import { ConflictSplitControl, type SplitDraft } from './ConflictSplitControl'
import {
  buildConflictPropertyRows,
  conflictSpec,
  conflictSplit,
  type ConflictSide,
  type ConflictSpec
} from './conflict-view-model'
import { entityDisplayName, formatLogicalId, relLabel, urnDisplayName } from './entity-display'

export { conflictSpec } from './conflict-view-model'
export type { ConflictSide, ConflictSpec, ConflictStructuralReason } from './conflict-view-model'

export interface BranchConflictViewProps {
  conflicts: MergeConflicts
  targetName: string
  sourceName: string
  choices?: Record<string, EntityResolution>
  /** Per-conflict agent rationale, rendered as a note where present. */
  rationales?: Record<string, string>
  disabled?: boolean
  /**
   * Logical ids whose conflict is fully resolved. Drives the "unresolved only"
   * filter and the unresolved count — omit to hide them.
   */
  resolvedIds?: ReadonlySet<string>
  /** Pick a side for the whole entity (the column-header shortcut). */
  onChooseSide?: (id: string, side: ConflictSide) => void
  /** Pick a side for one conflicting property. */
  onChooseField?: (id: string, field: string, side: ConflictSide) => void
  /** Set a corrected content value for one conflicting (scalar) property. */
  onChooseFieldValue?: (id: string, field: string, value: TaggedValue) => void
  /** Split an add/add node collision into two nodes (keep one side; re-home the other). */
  onChooseSplit?: (id: string, keep: ConflictSide, newId: string) => void
}

// One page of conflict cards. The full set is always reachable by paging — a
// large merge is paginated, never capped.
const PAGE_SIZE = 10

export function BranchConflictView({
  conflicts,
  targetName,
  sourceName,
  choices = {},
  rationales,
  disabled = false,
  resolvedIds,
  onChooseSide,
  onChooseField,
  onChooseFieldValue,
  onChooseSplit
}: BranchConflictViewProps) {
  const listRef = React.useRef<HTMLDivElement>(null)
  const [unresolvedOnly, setUnresolvedOnly] = React.useState(false)
  const [page, setPage] = React.useState(0)

  // Nodes then rels as one ordered list. ids are unique across kinds
  // (choices/specs key on id alone), so the id is a stable React key.
  const items = React.useMemo<ConflictItem[]>(
    () => [
      ...conflicts.nodes.map((conflict) => ({ kind: 'node' as const, conflict })),
      ...conflicts.rels.map((conflict) => ({ kind: 'relationship' as const, conflict }))
    ],
    [conflicts]
  )

  // Rows expand to the three-way resolution grid; several can be open at
  // once. Every conflict starts open — resolution is the page's whole job —
  // and a fresh conflict set (new preview, backstop) re-expands everything.
  const [openIds, setOpenIds] = React.useState<ReadonlySet<string>>(
    () => new Set(items.map((item) => item.conflict.id))
  )
  React.useEffect(() => {
    setOpenIds(new Set(items.map((item) => item.conflict.id)))
  }, [items])
  const setRowOpen = (id: string, open: boolean) =>
    setOpenIds((prev) => {
      const next = new Set(prev)
      if (open) next.add(id)
      else next.delete(id)
      return next
    })

  // Optionally narrow to unresolved cards; picks still apply to the full set
  // (the resolver holds them), so a card leaving the view as it resolves is
  // expected. Without resolvedIds the filter is inert.
  const displayItems = React.useMemo(
    () =>
      unresolvedOnly && resolvedIds
        ? items.filter((item) => !resolvedIds.has(item.conflict.id))
        : items,
    [items, unresolvedOnly, resolvedIds]
  )

  // Paginate the (filtered) set so the DOM holds at most one page of cards while
  // every conflict stays reachable by paging. safePage tolerates a set that
  // shrank (filter toggled, conflicts resolved out of view) since the last click.
  const pageCount = Math.max(1, Math.ceil(displayItems.length / PAGE_SIZE))
  const safePage = Math.min(page, pageCount - 1)
  const pageItems = displayItems.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE)

  // Each new page starts at the top of the list rather than wherever the last did.
  React.useEffect(() => {
    listRef.current?.scrollTo?.({ top: 0 })
  }, [safePage])

  const unresolvedCount = resolvedIds ? items.length - resolvedIds.size : items.length
  // The filter + pager earn their space once the set spans more than one page.
  const showControls = items.length > PAGE_SIZE

  return (
    <div className="flex min-h-0 flex-col gap-2">
      {showControls ? (
        <div className="flex flex-wrap items-center gap-3 text-11 text-ink-3">
          {resolvedIds ? (
            <>
              <span>
                <span className="font-medium text-ink">{unresolvedCount}</span> unresolved
              </span>
              <label className="flex cursor-pointer items-center gap-1.5 select-none">
                <input
                  type="checkbox"
                  checked={unresolvedOnly}
                  onChange={(event) => {
                    setUnresolvedOnly(event.target.checked)
                    setPage(0)
                  }}
                  className="size-3 cursor-pointer accent-brand"
                />
                Unresolved only
              </label>
            </>
          ) : null}
          <div className="ml-auto flex items-center gap-2">
            <span>
              Page {safePage + 1} of {pageCount}
            </span>
            <button
              type="button"
              onClick={() => setPage(Math.max(0, safePage - 1))}
              disabled={safePage === 0}
              className="cursor-pointer rounded-sm border border-line px-2 py-0.5 hover:text-ink disabled:cursor-default disabled:opacity-50"
            >
              Prev
            </button>
            <button
              type="button"
              onClick={() => setPage(Math.min(pageCount - 1, safePage + 1))}
              disabled={safePage >= pageCount - 1}
              className="cursor-pointer rounded-sm border border-line px-2 py-0.5 hover:text-ink disabled:cursor-default disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      ) : null}

      <div ref={listRef} className="flex min-h-0 flex-col gap-2 overflow-y-auto">
        {displayItems.length === 0 ? (
          <p className="px-2.5 py-2 text-xs text-ink-3">All conflicts resolved.</p>
        ) : (
          <DiffTable kindHeader="Change" entityKindHeader="Kind">
            {pageItems.map((item) => {
              const c = item.conflict
              const name =
                item.kind === 'node'
                  ? item.conflict.ours
                    ? entityDisplayName(item.conflict.ours)
                    : item.conflict.theirs
                      ? entityDisplayName(item.conflict.theirs)
                      : urnDisplayName(item.conflict.id)
                  : (relConflictName(
                      item.conflict.ours ?? item.conflict.theirs ?? item.conflict.base
                    ) ?? urnDisplayName(item.conflict.id))
              const type =
                item.kind === 'node'
                  ? ((item.conflict.ours ?? item.conflict.theirs ?? item.conflict.base)
                      ?.labels?.[0] ?? 'Node')
                  : ((item.conflict.ours ?? item.conflict.theirs ?? item.conflict.base)?.type ??
                    'Relationship')
              return (
                <ConflictRow
                  key={c.id}
                  id={c.id}
                  name={name}
                  type={type}
                  kind={item.kind}
                  base={c.base?.properties}
                  baseAbsent={c.base === null}
                  ours={c.ours?.properties}
                  oursAbsent={c.ours === null}
                  theirs={c.theirs?.properties}
                  theirsAbsent={c.theirs === null}
                  labels={item.kind === 'node' ? nodeLabelsRow(item.conflict) : undefined}
                  targetName={targetName}
                  sourceName={sourceName}
                  choice={choices[c.id]}
                  rationale={rationales?.[c.id]}
                  spec={conflictSpec(c.ours, c.theirs, c.fields)}
                  resolved={resolvedIds?.has(c.id) ?? false}
                  disabled={disabled}
                  open={openIds.has(c.id)}
                  onOpenChange={(open) => setRowOpen(c.id, open)}
                  onChooseSide={onChooseSide ? (side) => onChooseSide(c.id, side) : undefined}
                  onChooseField={
                    onChooseField ? (field, side) => onChooseField(c.id, field, side) : undefined
                  }
                  onChooseFieldValue={
                    onChooseFieldValue
                      ? (field, value) => onChooseFieldValue(c.id, field, value)
                      : undefined
                  }
                  // Split applies to add/add nodes only; rels never receive it.
                  onChooseSplit={
                    item.kind === 'node' && onChooseSplit
                      ? (keep, newId) => onChooseSplit(c.id, keep, newId)
                      : undefined
                  }
                />
              )
            })}
          </DiffTable>
        )}
      </div>
    </div>
  )
}

// ConflictItem is one row in the flattened (nodes-then-rels) conflict list — the
// discriminated kind narrows base/ours/theirs to the node or relationship shape.
type ConflictItem =
  | { kind: 'node'; conflict: MergeConflicts['nodes'][number] }
  | { kind: 'relationship'; conflict: MergeConflicts['rels'][number] }

function relConflictName(rel: TaggedRel | null | undefined): string | undefined {
  return rel ? relLabel(rel) : undefined
}

/**
 * The labels row for a node conflict, when the three versions do not all
 * carry the same label set: each present side's sorted labels, with `differs`
 * marking an ours-vs-theirs divergence (the case that forces whole-entity
 * resolution). Undefined when every present side agrees — no row to show.
 */
function nodeLabelsRow(c: MergeConflicts['nodes'][number]): ConflictLabelsRow | undefined {
  const fmt = (labels?: string[]) => [...(labels ?? [])].sort().join(', ')
  const cells = {
    base: c.base === null ? undefined : fmt(c.base.labels),
    ours: c.ours === null ? undefined : fmt(c.ours.labels),
    theirs: c.theirs === null ? undefined : fmt(c.theirs.labels)
  }
  const present = [cells.base, cells.ours, cells.theirs].filter((v) => v !== undefined)
  if (new Set(present).size <= 1) return undefined
  return {
    ...cells,
    differs: c.ours !== null && c.theirs !== null && cells.ours !== cells.theirs
  }
}

function ConflictRow({
  id,
  name,
  type,
  kind,
  base,
  baseAbsent,
  ours,
  oursAbsent,
  theirs,
  theirsAbsent,
  labels,
  targetName,
  sourceName,
  choice,
  rationale,
  onChooseFieldValue,
  onChooseSplit,
  spec,
  resolved,
  disabled,
  open,
  onOpenChange,
  onChooseSide,
  onChooseField
}: {
  id: string
  name: string
  /** The type column — a node label or relationship type. */
  type: string
  kind: 'node' | 'relationship'
  base?: Record<string, unknown>
  baseAbsent: boolean
  ours?: Record<string, unknown>
  oursAbsent: boolean
  theirs?: Record<string, unknown>
  theirsAbsent: boolean
  /** The labels row, when the node's label sets don't all agree. */
  labels?: ConflictLabelsRow
  targetName: string
  sourceName: string
  choice?: EntityResolution
  rationale?: string
  spec: ConflictSpec
  resolved: boolean
  disabled: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  onChooseSide?: (side: ConflictSide) => void
  onChooseField?: (field: string, side: ConflictSide) => void
  onChooseFieldValue?: (field: string, value: TaggedValue) => void
  onChooseSplit?: (keep: ConflictSide, newId: string) => void
}) {
  const rows = React.useMemo(
    () =>
      buildConflictPropertyRows({
        base,
        baseAbsent,
        ours,
        oursAbsent,
        theirs,
        theirsAbsent,
        // Whole-entity conflicts have no pickable fields; rows fall back to a
        // structural display comparison so a removed side's content shows.
        conflictingFields: spec.wholeEntityOnly ? undefined : spec.fields
      }),
    [base, baseAbsent, ours, oursAbsent, theirs, theirsAbsent, spec]
  )

  // Composite relationship ids render in the arrow form (`start —TYPE→ end`);
  // node URNs pass through unchanged.
  const displayId = formatLogicalId(id)
  const interactive = !disabled

  const [showUnchanged, setShowUnchanged] = React.useState(false)

  // The content-value editor is open for at most one field at a time per entity.
  const [editing, setEditing] = React.useState<ConflictValueEditorState | null>(null)
  const openEditor = (field: string, kind: ConflictValueEditorState['kind'], text: string) =>
    setEditing({ field, kind, text })
  const confirmEditor = () => {
    if (!editing || !onChooseFieldValue) return
    const res = encodeEditedScalar(editing.kind, editing.text)
    if (!res.ok) {
      setEditing({ ...editing, error: res.error })
      return
    }
    onChooseFieldValue(editing.field, res.value)
    setEditing(null)
  }

  // An add/add collision (a node both branches newly added, no base) can be
  // resolved by splitting it into two nodes.
  const addAdd = kind === 'node' && baseAbsent && !oursAbsent && !theirsAbsent
  const currentSplit = conflictSplit(choice)
  const [splitDraft, setSplitDraft] = React.useState<SplitDraft | null>(null)
  const openSplit = () =>
    setSplitDraft({ keep: currentSplit?.keep ?? 'ours', newId: currentSplit?.newId ?? `${id}-2` })
  const confirmSplit = () => {
    if (!splitDraft || !onChooseSplit) return
    const newId = splitDraft.newId.trim()
    if (!newId) {
      setSplitDraft({ ...splitDraft, error: 'Enter an id for the new node.' })
      return
    }
    if (newId === id) {
      setSplitDraft({ ...splitDraft, error: 'The new id must differ from the original.' })
      return
    }
    onChooseSplit(splitDraft.keep, newId)
    setSplitDraft(null)
  }

  // The row summary: resolution state first, else what resolving takes.
  const summary = resolved
    ? '✓ resolved'
    : spec.wholeEntityOnly
      ? '(structural)'
      : `(${spec.fields.length} field${spec.fields.length !== 1 ? 's' : ''} to resolve)`

  return (
    <DiffRow
      badge={<KindBadge tone="conflict">conflict</KindBadge>}
      type={type}
      entityKind={kind === 'relationship' ? 'rel' : 'node'}
      entity={name}
      entityTitle={displayId}
      summary={summary}
      open={open}
      onOpenChange={onOpenChange}
    >
      <div className="flex flex-col gap-1.5">
        <div className="px-2">
          <p className="truncate font-mono text-10 text-ink-3" title={displayId}>
            {displayId}
          </p>
          {rationale ? (
            <p className="mt-0.5 text-10 text-brand" title={rationale}>
              <span className="font-medium">Agent:</span> {rationale}
            </p>
          ) : null}
        </div>

        {addAdd && onChooseSplit ? (
          <ConflictSplitControl
            targetName={targetName}
            sourceName={sourceName}
            current={currentSplit}
            draft={splitDraft}
            interactive={interactive}
            onOpen={openSplit}
            onDraftChange={setSplitDraft}
            onConfirm={confirmSplit}
            onCancel={() => setSplitDraft(null)}
          />
        ) : null}

        {/* Property-aligned diff: one row per property, the conflicting fields
            (where the two sides disagree) highlighted and individually pickable,
            identical fields dimmed and merged as-is. The trailing column holds a
            per-field "edit value" affordance for scalar conflicts. */}
        <ConflictDiffGrid
          rows={rows}
          labelsRow={labels}
          targetName={targetName}
          sourceName={sourceName}
          choice={choice}
          wholeEntityOnly={spec.wholeEntityOnly}
          structuralReason={spec.reason}
          interactive={interactive}
          showUnchanged={showUnchanged}
          editing={editing}
          onToggleUnchanged={() => setShowUnchanged((v) => !v)}
          onChooseSide={onChooseSide}
          onChooseField={onChooseField}
          onChooseFieldValue={onChooseFieldValue}
          onOpenEditor={openEditor}
          onEditingChange={setEditing}
          onConfirmEditor={confirmEditor}
          onCancelEditor={() => setEditing(null)}
        />
      </div>
    </DiffRow>
  )
}
